import lod_ from "lodash";
import { t } from "i18next";

import { Drawer, Icon, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import { useSelector } from "react-redux";
import { replaceInTextHbr } from "helpers/utilities";
import { useState, memo } from "react";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";

/**
 * Dataset pannel view component
 * @param {*} param0 - open, onClose, data
 * @param {boolean} param0.open - open state
 * @param {function} param0.onClose - close function
 * @param {object} param0.data - data to display
 * @returns {JSX.Element}
 */
const DatasetViewPannel = memo(({ open, onClose, data = {} }) => {
	const emptyData = lod_.isNil(data) || lod_.isEmpty(data);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			style={{
				zIndex: 1300
			}}
			PaperProps={{
				sx: { width: "60%", display: "flex" }
			}}
		>
			{!emptyData && (
				<PannelContent
					chart={data.chart}
					dataset={data.dataset ?? {}}
					filters={data.filters ?? {}}
					resource={data.resource}
					dictionary={data.dictionary}
				/>
			)}
		</Drawer>
	);
});

/**
 * Pannel content component
 * @param {*} param0 - dataset, chart, filters
 * @param {object} param0.dataset - dataset object
 * @param {object} param0.chart - chart object
 * @param {object} param0.filters - filters object
 * @param {object} param0.resource - resource object
 * @param {object} param0.dictionary - dictionary object
 * @returns {JSX.Element} - Pannel content
 */
const PannelContent = memo(({ dataset, chart, filters, resource, dictionary }) => {
	const profile = useSelector(selectCurrentProfile);
	const [menuRef, setMenuRef] = useState(null);
	const [menuValues, setMenuValues] = useState({});

	const events = resource?.events ?? [];
	const analyticsEvents = events.filter(e => e.type === "analytics");

	/**
	 * Process action
	 * @param {string} action - action to process
	 * @param {object} config - action config
	 */
	const processAction = (action, config) => {
		switch (action) {
			case "goToUrl": {
				const url = replaceInTextHbr(config.url, {
					...menuValues,
					assistantID: profile?.assistantID
				});
				window.open(url, "_blank");
				break;
			}
			default:
				break;
		}
	};

	return (
		<MDBox p={2} sx={{ overflowY: "auto", flex: 1, display: "flex", flexDirection: "column" }}>
			{/* Header */}
			<MDBox display="flex" flexDirection="row" justifyContent="space-between">
				<MDTypography variant="h5">{dataset.label}</MDTypography>
				<MDTypography variant="subtitle2">
					{chart.data?.count ?? 0} {t("SETTINGS.results")}
				</MDTypography>
			</MDBox>
			{/* Content */}

			<MDBox sx={{ overflowY: "auto", flex: 1 }}>
				<DefaultDataTable
					compact
					noMargin
					noHeader
					list={chart}
					canSearch
					table={chart.data}
					display={chart.request.attributesDisplay}
					filter={filters}
					actions={[
						<Tooltip
							placement="top"
							title={
								lod_.isEmpty(analyticsEvents)
									? "Aucune actions, vous pouvez en ajouter depuis l'interface de ressource."
									: ""
							}
						>
							<IconButton
								handleclick={({ values }, e) => {
									if (!lod_.isEmpty(analyticsEvents)) {
										setMenuValues(values);
										setMenuRef(e.currentTarget);
									}
								}}
							>
								<Icon fontSize="medium">more_vert</Icon>
							</IconButton>
						</Tooltip>
					]}
					pagination={chart.pagination}
				/>
			</MDBox>
			<Menu anchorEl={menuRef} open={Boolean(menuRef)} onClose={() => setMenuRef(null)}>
				{analyticsEvents.map((event, index) => (
					<MenuItem
						key={index}
						onClick={({ values }, e) => {
							processAction(event.config.action || event.code, event.config);
							setMenuRef(null);
						}}
					>
						{event.icon && (
							<>
								<Icon fontSize="medium">{event.icon}</Icon>&nbsp;
							</>
						)}
						{event.description}
					</MenuItem>
				))}
			</Menu>
		</MDBox>
	);
});

export default DatasetViewPannel;
